import { Link, useLocation } from "@tanstack/react-router";
import { HelpCircle } from "lucide-react";
import { cn } from "@/lib/cn";
import { articleForRoute } from "./docsContent";

/**
 * A contextual "?" help affordance for an app surface. Resolves the current
 * route to its matching help article (via each article's `routes` front matter)
 * and links straight to it. Renders nothing if no article covers this route, so
 * it's safe to drop onto any shell header.
 *
 * Pass an explicit `route` to override auto-detection (e.g. a tabbed surface
 * that wants the article for a specific sub-tab). Auto-detection includes the
 * `?tab=` query, so a tab-qualified help route (e.g. `/coach?tab=roster`)
 * resolves to its specific guide.
 */
export function HelpLink({ route, className }: { route?: string; className?: string }) {
  const location = useLocation();
  // Include the search string so a tab-qualified help route can match.
  const here = `${location.pathname ?? "/"}${location.searchStr ?? ""}`;
  const article = articleForRoute(route ?? here);
  if (!article) return null;
  return (
    <Link
      to="/docs/$slug"
      params={{ slug: article.slug }}
      data-testid="surface-help-link"
      aria-label={`Help: ${article.title}`}
      title={`Help: ${article.title}`}
      className={cn(
        "inline-flex size-8 items-center justify-center rounded text-ink-muted transition-colors hover:bg-field hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      <HelpCircle className="size-4" />
    </Link>
  );
}
